import React from 'react';
import { theme } from '../theme';
import { ageText, arrivalHoverText } from '../utils/dateFormatting';
import { InfoTooltip } from './InfoTooltip';
import { orderedFlags } from './FlagPill';

// <purpose>THE ONE CLOCK of a list row (part 7 fix loop, 2026-10-12, operator ruling): the mail's
// arrival exists ONCE on screen and ONCE on hover, both on this element. The face is the COMPACT
// relative age (ageText's `compact` — „3 Min.", „5 Std.", „heute", „2 Tg.", „3 Wo.", „2 Mon.";
// fix round 1, operator ruling), right beside the unread dot on row 1; the hover is the
// full stamp („Eingang DD.MM.YY HH:MM", arrivalHoverText). Every other place that used to state
// the arrival — the subject hover on both lists, the single-kind row-3 stamp, the date inside the
// flag pill's hover — was deleted with this ruling, so this element is the only answer to „when did
// it come in".</purpose>
// <constraints>Mounted by the pages that paint row 1 (Antworten, the Archiv ledger's line 1).
// The upload clock is honoured: when ageText falls back to our own receipt stamp (the customer's
// stamp missing, in the future, or implausibly old) the hover says „Eingang laut Upload …" — the
// same words the flag hover used before this ruling moved them here. The danger first-day clock
// (elapsed minutes/hours) keys off the row's flags through orderedFlags — the SAME derivation the
// row's flag pills use, over the list row's `flags`, which core serves ENFORCED-only (a
// recorded-only flag is a detail fact and never reaches a list row). Layout: muted navy, tabular
// numerals, ONE fixed width sized to the longest compact face („100 Mon.", 8 characters) and
// flexShrink 0, so no row drifts from another, and neither a changing age nor the unread dot
// appearing beside it moves anything; it fades in like every new element. No German is spelled here beyond the
// stamp words the helpers already emit.</constraints>
export const ARRIVAL_AGE_WIDTH = '4.75em';

export const ArrivalAge: React.FC<{
  /** The customer's arrival stamp (TicketListItem.receivedAt). */
  receivedAt?: string | null;
  /** Our receipt stamp — the fallback clock (see ageText). */
  createdAt?: string | null;
  /** The list row's flag ids (enforced-only on the wire) — read for the danger first-day clock. */
  flags?: readonly string[] | null;
}> = ({ receivedAt, createdAt, flags }) => {
  const danger = orderedFlags(flags).includes('danger');
  const age = ageText(receivedAt, createdAt, undefined, danger);
  const hover = arrivalHoverText(receivedAt, createdAt);
  return (
    <InfoTooltip text={hover}>
      <span
        data-testid="arrival-age"
        data-clock={age.clock}
        style={{
          flexShrink: 0, width: ARRIVAL_AGE_WIDTH, minWidth: ARRIVAL_AGE_WIDTH, textAlign: 'right', whiteSpace: 'nowrap',
          fontSize: 11, fontWeight: 400, color: theme.colors.navyMuted,
          fontVariantNumeric: 'tabular-nums', animation: 'stx-fadeIn 0.3s ease',
        }}
      >
        {age.compact}
      </span>
    </InfoTooltip>
  );
};
